/**
 * Onde a sessão mora — e o que isso custa (F-S5, T-35).
 *
 * Access **e** refresh ficam em `localStorage`: é o que faz o login valer em
 * todas as abas e sobreviver ao fechamento do navegador. O preço, dito sem
 * rodeio: `localStorage` é legível por qualquer script que rode nesta origem —
 * um XSS (dependência npm comprometida, extensão do navegador) lê os dois
 * tokens. Trocar por cookie `HttpOnly` é decisão maior (CORS com credenciais,
 * embed cross-origin, regra 10 da arquitetura) e **não é esta tarefa**. A
 * mitigação real é dupla: a revogação por `token_version` do T-31 (F-S2,
 * entregue — trocar a senha invalida tudo o que foi emitido) e o cabeçalho de
 * CSP do T-53, que fica para a F-S3. O que dá para fazer aqui é fazer o logout
 * de uma aba atravessar para as outras — ver `SessionAcrossTabs`.
 */
export const ACCESS_TOKEN_KEY = "linkflow.access_token";
export const REFRESH_TOKEN_KEY = "linkflow.refresh_token";
/** As chaves que o listener de `storage` reconhece como sessão. */
export const SESSION_STORAGE_KEYS: readonly string[] = [ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY];

let accessTokenMemory: string | null = null;

function canUseStorage(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function readStoredToken(key: string): string | null {
  if (!canUseStorage()) return null;
  return window.localStorage.getItem(key);
}

function writeStoredToken(key: string, token: string | null): void {
  if (!canUseStorage()) return;
  if (!token) {
    window.localStorage.removeItem(key);
    return;
  }
  window.localStorage.setItem(key, token);
}

export function getAccessToken(): string | null {
  if (!accessTokenMemory) {
    accessTokenMemory = readStoredToken(ACCESS_TOKEN_KEY);
  }
  return accessTokenMemory;
}

export function setAccessToken(token: string | null): void {
  accessTokenMemory = token;
  writeStoredToken(ACCESS_TOKEN_KEY, token);
}

/**
 * Relê os dois tokens do storage e realinha a memória desta aba com o que está
 * lá. É o que o listener de `storage` chama (T-35): o evento vem de OUTRA aba,
 * e `getAccessToken` nunca relê o storage depois de popular a memória — sem
 * isto, a aba esquecida seguiria mandando o access antigo.
 */
export function syncSessionTokensFromStorage(): { access: string | null; refresh: string | null } {
  const access = readStoredToken(ACCESS_TOKEN_KEY);
  accessTokenMemory = access;
  return { access, refresh: readStoredToken(REFRESH_TOKEN_KEY) };
}

export function getRefreshToken(): string | null {
  if (!canUseStorage()) return null;
  const local = window.localStorage.getItem(REFRESH_TOKEN_KEY);
  if (local) return local;

  // Migração de sessões antigas: refresh token estava no sessionStorage.
  const legacy = window.sessionStorage.getItem(REFRESH_TOKEN_KEY);
  if (!legacy) return null;
  window.localStorage.setItem(REFRESH_TOKEN_KEY, legacy);
  window.sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  return legacy;
}

export function setRefreshToken(token: string | null): void {
  writeStoredToken(REFRESH_TOKEN_KEY, token);

  // Cleanup de compatibilidade com versão antiga.
  if (typeof window !== "undefined" && typeof window.sessionStorage !== "undefined") {
    if (!token) {
      window.sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    }
  }
}

export function clearSessionTokens(): void {
  setAccessToken(null);
  setRefreshToken(null);
}
